import Button from '@/componentsv2/Button';
import Dialog from '@/componentsv2/Dialog';

import { type ReorderRowStatus, diffOrderedItems } from './reorderDiff';

interface ViewChangesModalProps {
  open: boolean;
  onClose: () => void;
  fieldLabel: string;
  oldValue: unknown;
  newValue: unknown;
}

function formatResponseType(rt: unknown): string {
  if (rt === 'yes_no') return 'Yes/No';
  if (rt === 'free_form') return 'Free-form';
  return String(rt);
}

// One diffable line per array item. Vetting query snapshots
// ({ text, response_type, required, hidden }) render as the question text with a
// compact attribute suffix; plain values (e.g. company names) render verbatim.
function itemLabel(x: unknown): string {
  if (x && typeof x === 'object' && !Array.isArray(x)) {
    const o = x as Record<string, unknown>;
    if ('text' in o) {
      const attrs = [
        o.required ? 'required' : null,
        o.response_type ? formatResponseType(o.response_type) : null,
      ].filter(Boolean);
      return attrs.length ? `${String(o.text)} (${attrs.join(', ')})` : String(o.text);
    }
    // Unknown object shape: don't enumerate arbitrary keys (leaks internal metadata
    // and coerces nested objects to "[object Object]"). Show a generic placeholder.
    return '(complex value)';
  }
  return String(x);
}

function toItemLabels(v: unknown): string[] {
  return Array.isArray(v) ? v.map(itemLabel) : [];
}

// The reorder view shows just the question text — the attribute suffix is noise
// when the point is the order.
function itemText(x: unknown): string {
  if (x && typeof x === 'object' && !Array.isArray(x)) {
    const o = x as Record<string, unknown>;
    if ('text' in o) return String(o.text);
    return '(complex value)';
  }
  return String(x);
}

function toItemTexts(v: unknown): string[] {
  return Array.isArray(v) ? v.map(itemText) : [];
}

// Render arbitrary JSON old/new values per type — never a raw JSON dump.
function scalarText(v: unknown): string {
  if (v === null || v === undefined || v === '') return '(empty)';
  if (typeof v === 'object') {
    return Object.entries(v as Record<string, unknown>)
      .map(([k, val]) => `${k}: ${String(val)}`)
      .join('\n');
  }
  return String(v);
}

function ArrayDiff({ oldValue, newValue }: { oldValue: unknown; newValue: unknown }): JSX.Element {
  const before = new Set(toItemLabels(oldValue));
  const after = new Set(toItemLabels(newValue));
  const added = [...after].filter((x) => !before.has(x));
  const removed = [...before].filter((x) => !after.has(x));

  if (added.length === 0 && removed.length === 0) {
    return <div className="text-on-surface-secondary body-1">No items changed.</div>;
  }
  return (
    <div className="flex flex-col gap-4 body-1">
      {added.map((x, i) => (
        <div key={`added-${i}`} className="text-alert-success dark:text-green-300">
          + {x}
        </div>
      ))}
      {removed.map((x, i) => (
        <div key={`removed-${i}`} className="text-on-surface-muted line-through">
          - {x}
        </div>
      ))}
    </div>
  );
}

// A vetting-query array (objects with attributes) is ordered — reordering it is a
// real change. A plain-value array (e.g. company names) is treated as a set.
function isRecordArray(v: unknown): boolean {
  return Array.isArray(v) && v.some((x) => x !== null && typeof x === 'object');
}

function rowClass(status: ReorderRowStatus, side: 'before' | 'after'): string {
  switch (status) {
    case 'moved':
      return side === 'before'
        ? 'text-alert-error font-semibold'
        : 'text-brand-primary font-semibold';
    case 'removed':
      return 'text-on-surface-muted line-through';
    case 'added':
      return 'text-alert-success dark:text-green-300';
    default:
      return 'text-on-surface';
  }
}

// Ordered (vetting-query) arrays render as before/after numbered lists so a reorder
// is visible; only the item that actually moved is highlighted — red at its old
// position, teal at its new one — while rows that merely shifted stay neutral.
function OrderedArrayDiff({
  oldValue,
  newValue,
}: {
  oldValue: unknown;
  newValue: unknown;
}): JSX.Element {
  const { before, after } = diffOrderedItems(toItemTexts(oldValue), toItemTexts(newValue));
  return (
    <div className="flex flex-col gap-16">
      <div className="flex flex-col gap-2">
        <span className="text-12 font-semibold uppercase text-alert-error">Before</span>
        <ol className="flex flex-col gap-4 body-1">
          {before.map((row, i) => (
            <li key={`before-${i}`} className={rowClass(row.status, 'before')}>
              {i + 1}. {row.label}
            </li>
          ))}
        </ol>
      </div>
      <div className="flex flex-col gap-2">
        <span className="text-12 font-semibold uppercase text-brand-primary">After</span>
        <ol className="flex flex-col gap-4 body-1">
          {after.map((row, i) => (
            <li key={`after-${i}`} className={rowClass(row.status, 'after')}>
              {i + 1}. {row.label}
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

export default function ViewChangesModal({
  open,
  onClose,
  fieldLabel,
  oldValue,
  newValue,
}: ViewChangesModalProps): JSX.Element {
  const isArray = Array.isArray(oldValue) || Array.isArray(newValue);
  const isOrdered = isRecordArray(oldValue) || isRecordArray(newValue);

  let body: JSX.Element;
  if (isArray && isOrdered) {
    body = <OrderedArrayDiff oldValue={oldValue} newValue={newValue} />;
  } else if (isArray) {
    body = <ArrayDiff oldValue={oldValue} newValue={newValue} />;
  } else {
    body = (
      <div className="flex flex-col gap-16">
        <div className="flex flex-col gap-2">
          <span className="text-12 font-semibold uppercase text-alert-error">Before</span>
          <span className="whitespace-pre-line text-on-surface body-1">{scalarText(oldValue)}</span>
        </div>
        <div className="flex flex-col gap-2">
          <span className="text-12 font-semibold uppercase text-brand-primary">After</span>
          <span className="whitespace-pre-line text-on-surface body-1">{scalarText(newValue)}</span>
        </div>
      </div>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      disablePortal={false}
      aria-labelledby="er-view-changes-title"
    >
      <div data-testid="er-view-changes" className="flex flex-col gap-16 p-24">
        <h2 id="er-view-changes-title" className="hd-5">
          {fieldLabel}
        </h2>

        {body}

        <div className="flex justify-end">
          <Button variant="tertiary" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
